import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import { amountToInteger } from '@actual-app/core/shared/util';

import { Boton } from '#components/mobile/ui/Boton';
import { ImporteMovimiento } from '#components/mobile/ui/ImporteMovimiento';
import { Tarjeta } from '#components/mobile/ui/Tarjeta';
import { color, texto } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useLocale } from '#hooks/useLocale';

import { BocadilloGavi } from './BocadilloGavi';
import type { FilaVista } from './importar';
import type { Parecido } from './parecidos';

export type Respuesta = 'mismo' | 'distinto';

type DudasRepetidosProps = {
  parecidos: Parecido[];
  filas: FilaVista[];
  respuestas: Map<string, Respuesta>;
  onResponder: (fila: string, respuesta: Respuesta) => void;
  /** Cuenta a la que se importa. */
  cuenta: string;
  nombreComercio: (payee: string) => string | undefined;
  nombreCuenta: (cuenta: string) => string | undefined;
};

/**
 * «¿Ya lo tenías?»: cada movimiento del banco que se parece a uno apuntado a
 * mano, uno junto al otro, y la pregunta. Gavi no decide sola: dos cobros
 * iguales pueden ser reales.
 */
export function DudasRepetidos({
  parecidos,
  filas,
  respuestas,
  onResponder,
  cuenta,
  nombreComercio,
  nombreCuenta,
}: DudasRepetidosProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const porId = new Map(filas.map(f => [f.id, f]));
  const fecha = (dia: string) => monthUtils.format(dia, 'd MMM', locale);

  return (
    <View
      style={{ gap: 10, '& > *': { flexShrink: 0 } }}
      data-testid="importar-dudas"
    >
      <BocadilloGavi estado="ladea">
        {t(
          'These look like ones you entered yourself. Tell me if they are the same one, so they are not counted twice.',
        )}
      </BocadilloGavi>
      {parecidos.map(p => {
        const fila = porId.get(p.fila);
        if (!fila) {
          return null;
        }
        const respuesta = respuestas.get(p.fila);
        const otraCuenta =
          p.apuntado.cuenta !== cuenta
            ? nombreCuenta(p.apuntado.cuenta)
            : undefined;
        const dias = Math.abs(
          monthUtils.differenceInCalendarDays(fila.fecha, p.apuntado.fecha),
        );
        const motivo =
          p.motivo === 'importe'
            ? t('Similar amount')
            : p.motivo === 'fecha'
              ? dias === 0
                ? t('Same amount, same day')
                : t('Same amount, {{count}} days apart', { count: dias })
              : t('Same amount, in {{account}}', {
                  account: otraCuenta ?? '',
                });
        return (
          <Tarjeta key={p.fila} style={{ gap: 10 }} data-testid="importar-duda">
            <Text
              data-testid="importar-duda-motivo"
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: color.warn,
                alignSelf: 'flex-start',
                backgroundColor: color.warnSoft,
                borderRadius: 10,
                padding: '2px 8px',
              }}
            >
              {motivo}
            </Text>
            <Linea
              etiqueta={t('Bank')}
              nombre={fila.nombre}
              fecha={fecha(fila.fecha)}
              importe={amountToInteger(fila.importe)}
            />
            <Linea
              etiqueta={t('You entered')}
              nombre={
                (p.apuntado.payee && nombreComercio(p.apuntado.payee)) || '—'
              }
              fecha={
                otraCuenta
                  ? `${fecha(p.apuntado.fecha)} · ${otraCuenta}`
                  : fecha(p.apuntado.fecha)
              }
              importe={p.apuntado.importe}
            />
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <Boton
                variante={respuesta === 'mismo' ? 'primario' : 'fantasma'}
                aria-pressed={respuesta === 'mismo'}
                onPress={() => onResponder(p.fila, 'mismo')}
                data-testid="importar-duda-mismo"
                style={{ flex: 1 }}
              >
                <Trans>It's the same</Trans>
              </Boton>
              <Boton
                variante={respuesta === 'distinto' ? 'primario' : 'fantasma'}
                aria-pressed={respuesta === 'distinto'}
                onPress={() => onResponder(p.fila, 'distinto')}
                data-testid="importar-duda-distinto"
                style={{ flex: 1 }}
              >
                <Trans>They're different</Trans>
              </Boton>
            </View>
            {respuesta && (
              <Text style={{ fontSize: 12, color: color.fg2 }}>
                {respuesta === 'mismo'
                  ? t(
                      "I keep the bank's one, which is how it will come next time, and remove yours. Your name goes in its notes and your category stays.",
                    )
                  : t('Both stay: two real charges.')}
              </Text>
            )}
          </Tarjeta>
        );
      })}
    </View>
  );
}

function Linea({
  etiqueta,
  nombre,
  fecha,
  importe,
}: {
  etiqueta: string;
  nombre: string;
  fecha: string;
  importe: number;
}) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
      <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
        <Text style={{ fontSize: 12, color: color.fg2 }}>{etiqueta}</Text>
        <Text
          style={{
            ...texto.fila,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {nombre || '—'}
        </Text>
        <Text style={{ fontSize: 12, color: color.fg2 }}>{fecha}</Text>
      </View>
      <PrivacyFilter>
        <ImporteMovimiento valor={importe} />
      </PrivacyFilter>
    </View>
  );
}
